import { LoadingOutlined } from "@ant-design/icons";
import {
  Col,
  Pagination,
  Row,
  Spin,
  ConfigProvider,
  DatePicker,
  Badge,
} from "antd";
import Table, { ColumnsType } from "antd/es/table";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "components/SkeletonsLoading/SkeletonTable";
import PageTitle from "components/UI/PageTitle";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import format from "utils/helpers/date-format";
import { useWidth } from "utils/helpers/use-is-mobile";
import { hasPermission } from "components/permissionsGate";
import useTechnicanScheduledAppointments from "hooks/query/users/useTechnicanScheduledAppointments";
import { TicketModel } from "models/tickets";
import { TechnicanSceduledAppointment } from "models/technicians";
import FutureDateCounter from "components/DateCounter/FutureDateCounter";
import { useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;
const future20 = dayjs().add(20, "day").format("YYYY-MM-DD");
const defaultDateRange = `${dayjs().format("YYYY-MM-DD HH:mm")},${future20}`;

const ScheduledAppointments = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(8);
  const [date, setDate] = useState(defaultDateRange);
  const startDate = date.split(",")[0];
  const endDate = date.split(",")[1];

  const { data, isLoading, isFetching } = useTechnicanScheduledAppointments(
    startDate,
    endDate,
    page,
    perPage
  );
  const { t, i18n: i18next } = useTranslation();

  const { isMobile, innerWidth } = useWidth();
  const navigate = useNavigate();

  const antIcon = <LoadingOutlined style={{ fontSize: 40 }} spin />;

  const ChangeDate = (e: any) => {
    if (e === null) {
      setDate?.("");
    } else if (e[0] !== null && e[1] !== null && e !== null) {
      setDate?.(
        e[0].format("YYYY-MM-DD HH:mm") + "," + e[1].format("YYYY-MM-DD HH:mm")
      );
    } else {
      setDate?.("");
    }
  };

  const columns: ColumnsType<TechnicanSceduledAppointment> = [
    {
      title: t("name"),
      dataIndex: "",
      key: "full_name",
      width: "15%",
      align: "center",
      render: (data: TechnicanSceduledAppointment) => (
        <>
          {data?.status.id == 8 ? (
            <Badge.Ribbon
              text={t("denied")}
              color="red"
              placement="start"
              style={{
                background: "red",
                position: "absolute",
                top: "-20px",
                right: "-24px",
              }}
            >
              <div>{data?.ticket.user?.full_name}</div>
            </Badge.Ribbon>
          ) : (
            <p>{data?.ticket.user?.full_name}</p>
          )}
        </>
      ),
    },
    {
      title: <span style={{ marginLeft: "5px" }}>{t("technicals")}</span>,
      dataIndex: "ticket",
      width: "15%",
      align: "center",
      render: (ticket: TicketModel) => (
        <span>
          {ticket.technicals?.map((technician) => (
            <p>{technician?.full_name}</p>
          ))}
        </span>
      ),
    },
    {
      title: t("time"),
      dataIndex: "time",
      key: "time",
      width: "15%",
      align: "center",
      render: (time: string) => (
        <>
          {time ? (
            dayjs(time).isValid() ? (
              <span style={{ direction: "ltr", display: "block" }}>
                {dayjs(time).format(format.date_time)}
              </span>
            ) : (
              ""
            )
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      title: t("time_until"),
      dataIndex: "time",
      key: "time_until",
      width: "15%",
      align: "center",
      render: (time: string) =>
        time ? (
          dayjs(time).isValid() ? (
            <span style={{ direction: "ltr", display: "block" }}>
              <FutureDateCounter date={time} />
            </span>
          ) : (
            ""
          )
        ) : (
          "-"
        ),
    },
  ];

  const deviceColumns: ColumnsType<TechnicanSceduledAppointment> = [
    {
      render: (obj: TechnicanSceduledAppointment) => {
        return (
          <div
            style={{
              boxShadow: "rgba(0, 0, 0, 0.15) 0px 5px 15px 0px",
              padding: "0",
              borderRadius: "18px",
              overflow: "hidden",
            }}
          >
            <div className="lable">
              <>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "1rem",
                    backgroundColor: "#ccc",
                    padding: "0.3rem 0",
                  }}
                >
                  <span>{t("name")}:</span>
                  <span
                    style={{
                      width: "fit-content",
                      padding: "7px",
                      borderRadius: "50%",
                    }}
                  >
                    {obj.ticket?.user?.full_name}
                  </span>
                </div>
              </>
            </div>
            <Row style={{ padding: "20px", paddingTop: "0" }}>
              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("technicals")}: </span>
                  <span>
                    {obj.ticket.technicals?.map((technician) => (
                      <p>{technician?.full_name}</p>
                    ))}
                  </span>
                </div>
              </Col>
              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("time")}: </span>
                  {obj?.time ? (
                    dayjs(obj.time).isValid() ? (
                      <span style={{ direction: "ltr", display: "block" }}>
                        {dayjs(obj.time).format(format.date_time)}
                      </span>
                    ) : (
                      ""
                    )
                  ) : (
                    "-"
                  )}
                </div>
              </Col>
              <Col md={12} span={24}>
                <div className="label" style={{ marginBottom: "4px" }}>
                  <span>{t("time_until")}: </span>
                  {obj?.time ? (
                    dayjs(obj.time).isValid() ? (
                      <span style={{ direction: "ltr", display: "block" }}>
                        <FutureDateCounter date={obj.time} />
                      </span>
                    ) : (
                      ""
                    )
                  ) : (
                    "-"
                  )}
                </div>
              </Col>
            </Row>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <Row gutter={[25, 25]}>
        <Col span={24} order={1}>
          {data?.data && !isLoading && (
            <>
              <PageTitle>{t("appointments")}</PageTitle>
              <ConfigProvider direction="ltr">
                <RangePicker
                  style={{ marginLeft: "10px" }}
                  disabledDate={(current) => {
                    return (
                      current.isBefore(dayjs("2022-01-01")) ||
                      current.isAfter(dayjs().subtract(-100, "day"))
                    );
                  }}
                  value={
                    date !== ""
                      ? [dayjs(date?.split(",")[0]), dayjs(date?.split(",")[1])]
                      : undefined
                  }
                  onChange={(e: any) => ChangeDate(e)}
                  showTime
                  format={format.date}
                  allowClear={false}
                />
              </ConfigProvider>
              <div style={{ marginTop: "16px" }} className="techniciansBtns">
                <div>
                  <div className="finishBtn">
                    {t("number_of_appointments")}{" "}
                    <span>{data?.meta?.total}</span>
                  </div>
                </div>
              </div>

              <SkeletonTable
                loading={isLoading}
                columns={columns as SkeletonTableColumnsType[]}
              >
                <Table
                  dataSource={data?.data}
                  columns={!isMobile ? columns : deviceColumns}
                  pagination={false}
                  bordered={true}
                  rowClassName={"pointer"}
                  onRow={(record) => ({
                    onClick: () => navigate(`/tickets/${record.ticket.id}`),
                  })}
                />
              </SkeletonTable>

              <Pagination
                onChange={(e) => setPage(e)}
                current={page}
                defaultCurrent={1}
                defaultPageSize={8}
                total={data?.meta?.total}
                pageSize={perPage}
                onShowSizeChange={(current, size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            </>
          )}
          {isLoading && (
            <div style={{ height: "20vh" }} className="loadingCon">
              <Spin indicator={antIcon} size="large" />
            </div>
          )}
        </Col>
      </Row>
      {!hasPermission(["browse_schedules"]) && (
        <div
          style={{
            textAlign: "center",
            fontSize: 25,
            paddingBlock: "30vh",
          }}
        >
          {t("dont_have_pirmission")}
        </div>
      )}
    </>
  );
};

export default ScheduledAppointments;
